import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { HelperService } from 'src/app/services/helper/helper.service';
import { ErrorMonitoringService } from 'src/app/services/error-monitoring/error-monitoring.service';
import { AlertController, ModalController } from '@ionic/angular';
import * as moment from 'moment-timezone';
import { FileObject, FileTransport } from 'src/app/interfaces/general-objects';

/**
 * @required false
 * @description Modal para gestión de archivos
 * @example 
 * {
 *   fileStore: this.service.serviceData.formulas,
 *   title: "Sube tus documentos para el trámite",
 *   typeOfFiles: "image,portable",
 *   eventUpload: ['quotation', 'scheduled', 'started'].includes(this.service.status) ? this.uploadFormulas : null,
 *   eventDelete: ['quotation', 'scheduled'].includes(this.service.status) ? this.deleteFormula : null,
 *   service: this.servicesService,
 *   parentId: this.serviceId,
 *   folder: ['service_images', this.serviceId, 'formulas'],
 *   maxFiles: 10,
 * }
 *
 * @author Alejandro Rangel Moreno <erangel@asesoftware.com>
 * @date 2020-05-23
 * @export true
 * @class FilesComponent
 * @implements {OnInit}
 */
@Component({
  selector: 'app-files',
  templateUrl: './files.component.html',
  styleUrls: ['./files.component.scss'],
})
export class FilesComponent implements OnInit {

  /**
   * @required {false}
   * @default {'Adjuntar'}
   * @description Texto en el botón de adjuntar
   * @type {string}
   * @memberof FilesComponent
   */
  @Input() buttonTitle: string = "Adjuntar";
  /**
   * @required {false}
   * @default {null} -> No muestra el icono de borrar archivo
   * @description Evento para eliminar el archivo de base de datos
   * @example
   * async deleteFormula(fileTransport: FileTransport, serviceObject: ServiceService) {
   *   let service: any;
   *   // obtiene la entidad por el id
   *   const data = await serviceObject.getServicesByIdWithOutSubscription(fileTransport.parentId).toPromise();
   *   service = data.data();
   *   if (!service.serviceData.formulas) return;
   *   const listaFormulas = service.serviceData.formulas;
   *   const iFormulas = listaFormulas.findIndex((x: { originalName: string; }) => x.originalName === fileTransport.metadata.originalName);
   *   service.serviceData.formulas.splice(iFormulas, 1);
   *   await serviceObject.updateService(service, fileTransport.parentId);
   *   return serviceObject.deleteFile(fileTransport);
   * }
   * @type {Function}
   * @memberof FilesComponent
   */
  @Input() eventDelete: any;
  /**
   * @required {false}
   * @default {null} -> no muestra el botón de adjuntar archivos
   * @description Evento que carga el archivo en base de datos y en storage
   * @example 
   * async uploadFormulas(fileTransport: FileTransport, servicesService: ServiceService) {
   *   let service: any;
   *   // obtiene la entidad por el id
   *   const data = await servicesService.getServicesByIdWithOutSubscription(fileTransport.parentId).toPromise();
   *   service = data.data();
   *   if (!service.serviceData.formulas) service.serviceData.formulas = [];
   *   if (service.serviceData.formulas.length > 9 ||
   *     service.serviceData.formulas.some(
   *       (file: { originalName: string; name: string; }) => file.originalName === fileTransport.metadata.originalName && file.name === fileTransport.metadata.originalName)
   *   ) return;
   *   // actualiza el servicio
   *   service.serviceData.formulas.push(fileTransport.metadata);
   *   await servicesService.updateService(service, fileTransport.parentId);
   *   fileTransport.registered = true;
   *   return fileTransport;
   * }
   * @type {Function}
   * @memberof FilesComponent
   */
  @Input() eventUpload: any;
  /**
   * @required {false}
   * @default {[]} -> Muestra una lista vacía
   * @description Listado de archivos suscritos
   * @type {FileObject[]}
   * @memberof FilesComponent
   */
  @Input() fileStore: FileObject[] = [];
  /**
   * @required {false}
   * @default {['NoDefinido']} -> Crea una carpeta "NoDefinido" en fire storage donde almacena los archivos
   * @description Carpeta donde se deposita el archivo en fire storage
   * @example ['formulas',this.serviceId,'service_images']
   * @type {string}
   * @memberof FilesComponent
   */
  @Input() folder: string[] = ['NoDefinido'];
  /**
   * @required {false}
   * @default {1}
   * @description Máxima cantidad de archivos que se aceptan
   * @type {number}
   * @memberof FilesComponent
   */
  @Input() maxFiles: number = 1;
  /**
   * @required {false}
   * @default {5}
   * @description Tamaño máximo en MB del archivo admitido
   * @type {number}
   * @memberof FilesComponent
   */
  @Input() maxSize: number = 15;
  /**
   * @required true
   * @default {'NoDefinido'} -> Genera un error al no encontrar el identificador del contenedor del archivo
   * @description Es el identificador del padre o contenedor del archivo
   * @requires true
   * @type {string}
   * @memberof FilesComponent
   */
  @Input() parentId: string = 'NoDefinido';
  /**
   * @required false
   * @default {false} -> Solo permite visualizar las imágenes en un modal sin tener en cuenta los pdf
   * @description Bandera para habilitar funcionalidades de impresión para todos los archivos
   * @type {boolean}
   * @memberof FilesComponent
   */
  @Input() print: boolean = false;
  /**
   * @required false
   * @default {'Adjunta archivos en formato de imagen o PDF'} 
   * @description Texto para mostrar la restricción en los archivos
   * @type {string}
   * @memberof FilesComponent
   */
  @Input() restriction: string = "Adjunta archivos en formato de imagen o PDF";
  /**
   * @required false
   * @default {null} No ejecuta comandos que afectan la base de datos o el almacenamiento de archivos
   * @description Instancia del controlador de Base de datos
   * @type {*}
   * @memberof FilesComponent
   */
  @Input() service: any;
  /**
   * @required false
   * @default {'Lista de archivos'} 
   * @description Muestra el título del modal
   * @type {string}
   * @memberof FilesComponent
   */
  @Input() title: string = "Lista de archivos";
  /**
   * @required false
   * @default {"image,portable"} -> Filtra archivos de tipo imagen y portable
   * @description Tipo de archivos a recibir, se puede recibir varios tipos de archivos separados por coma: Las siguientes son las opciones que recibe:
   *  null = Toma predefinido imágenes y portables,
   *  empty = Acepta todos los archivos,
   *  image = solo imágenes,
   *  portable = solo portables,
   *  video = solo videos
   * @example "image,portable,video"
   * @type {string}
   * @memberof FilesComponent
   */
  @Input() typeOfFiles: string = "image,portable";

  /**
   * @required false
   * @description Listado de extensiones para tipos de archivo imágen
   * @private
   * @type {string[]}
   * @memberof FilesComponent
   */
  public images: string[] = ['jpg', 'jpeg', 'png', 'bmp', 'tiff', 'tif', 'svg', 'gif']
  /**
   * @description Listado de extensiones para tipos de archivos portables
   * @private
   * @type {string[]}
   * @memberof FilesComponent
   */
  private portables: string[] = ['pdf']
  /**
   * @description Listado de extensiones para tipos de archivo de video
   * @private
   * @type {string[]}
   * @memberof FilesComponent
   */
  private videos: string[] = ['mp4', 'wmv', 'avi', 'mov', 'aac']

  /**
   * @description Texto que restringe los archivos en el control input
   * @private
   * @type {string}
   * @memberof FilesComponent
   */
  public accept: string;
  /**
   * @description Definición del objeto de archivo
   *
   * @private
   * @type {FileObject}
   * @memberof FilesComponent
   */
  private fileObject: FileObject;
  /**
   * @description Máximo tamaño del archivo a cargar representado en bytes
   *
   * @private
   * @type {number}
   * @memberof FilesComponent
   */
  private maxSizeOfFile: number;
  /**
   * @description Listado de archivos en proceso
   *
   * @private
   * @type {FileObject[]}
   * @memberof FilesComponent
   */
  public processingFiles: FileObject[] = [];

  /**
   *Creates an instance of FilesComponent.
   * @author Alejandro Rangel Moreno <erangel@asesoftware.com>
   * @date 2020-05-23
   * @param {HelperService} helperService
   * @param {ErrorMonitoringService} errorMonitoringService
   * @param {AlertController} alertController
   * @param {ModalController} modalController
   * @memberof FilesComponent
   */
  constructor(
    private helperService: HelperService,
    private errorMonitoringService: ErrorMonitoringService,
    private alertController: AlertController,
    private modalController: ModalController,
  ) { }

  /**
   * @description Inicializa el componente
   *
   * @author Alejandro Rangel Moreno <erangel@asesoftware.com>
   * @date 2020-05-23
   * @returns this
   * @memberof FilesComponent
   */
  ngOnInit() {
    this.maxSizeOfFile = Number(this.maxSize) * 1000000;
    this.accept = '';
    let tempArray = [];
    if (!this.typeOfFiles) return;
    if (this.typeOfFiles.split(',').includes('image')) tempArray.push(this.getTypesOfFiles(this.images));
    if (this.typeOfFiles.split(',').includes('portable')) tempArray.push(this.getTypesOfFiles(this.portables));
    if (this.typeOfFiles.split(',').includes('video')) tempArray.push(this.getTypesOfFiles(this.videos));
    if (this.maxFiles === null || this.maxFiles < 1) this.maxFiles = 1;
    this.accept = tempArray.join(',');
  }

  /**
   * @description Elimina el archivo de base de datos y del storage
   *
   * @author Alejandro Rangel Moreno <erangel@asesoftware.com>
   * @date 2020-05-23
   * @param {FileObject} fileObject
   * @memberof FilesComponent
   */
  deleteFile(fileObject: FileObject) {
    // Se empaqueta la información para transportarla
    let transportData = {
      metadata: fileObject,
      folder: this.folder.join('/'),
      parentId: this.parentId,
    } as FileTransport;

    let iFormulas = this.fileStore.findIndex(x => x.originalName === transportData.metadata.originalName);
    this.fileStore.splice(iFormulas, 1);

    this.eventDelete(transportData, this.service);
  }

  /**
   * @description Remueve un elemento del listado de archivos
   *
   * @author Alejandro Rangel Moreno <erangel@asesoftware.com>
   * @date 2020-05-23
   * @param {FileObject} fileObject
   * @returns {void}
   * @memberof FilesComponent
   */
  removeItem(fileObject: FileObject): void {
    let i: number = this.processingFiles.findIndex(x => x.originalName === fileObject.originalName);
    if (i < 0) return;
    this.processingFiles.splice(i, 1);
  }

  /**
   * @description Vista previa del archivo
   *
   * @author Alejandro Rangel Moreno <erangel@asesoftware.com>
   * @date 2020-05-23
   * @param {FileObject} file
   * @memberof FilesComponent
   */
  async previewFile(file: FileObject) {
    if(this.print) return window.open(file.url,'blank');
    let objAlert = {
      header: null,
      message: null,
      buttons: ['Aceptar']
    };
    /** Controlador de alerta */
    objAlert.header = file.originalName;
    objAlert.message = `<img src="${file.url}" alt="vista previa" style="border-radius: 2px">`;

    const alert = await this.alertController.create(objAlert);
    await alert.present();
  }

  /**
   * @description Abre el enlace en una nueva ventana
   *
   * @author Nico Bernal <tech@medilabb.com>
   * @date 2021-01-06
   * @param {FileObject} file
   * @memberof FilesComponent
   */
  async openFile(file: FileObject) {
    window.open(file.url, 'blank');
  }

  /**
   * @description Genera los procesos posteriores a subir los archivos, confirmando que el archivo quedo subido
   *
   * @author Alejandro Rangel Moreno <erangel@asesoftware.com>
   * @date 2020-05-23
   * @param {FileTransport} fileTransport
   * @memberof FilesComponent
   */
  confirmUploadFile(fileTransport: FileTransport) {
    this.eventUpload(fileTransport, this.service, this.maxFiles).then((datosTransportados: { registered: any; metadata: FileObject; }) => {
      if (datosTransportados.registered) {
        this.removeItem(datosTransportados.metadata);
        this.fileStore.push(datosTransportados.metadata);
      }
    });
  }

  /**
   * @description Carga el archivo con los datos proporcionados
   *
   * @author Alejandro Rangel Moreno <erangel@asesoftware.com>
   * @date 2020-05-23
   * @param {{ metadata: FileObject; file: File; }} { metadata, file }
   * @memberof FilesComponent
   */
  async uploadFile({ metadata, file }: { metadata: FileObject; file: File; }) {
    // Anexo la metadata al listado de archivos en proceso.
    this.processingFiles.push(metadata);

    //re-nombra el archivo que se va a guardar
    Object.defineProperty(file, 'name', {
      writable: true,
      value: metadata.name + '.' + metadata.extension,
    });

    // Se empaqueta la información para transportarla
    let transportData = {
      metadata: metadata,
      file: file,
      parentId: this.parentId,
    } as FileTransport;

    await this.service.uploadFile(transportData).then(fileUrl => {
      transportData.metadata.url = fileUrl as string;
      this.confirmUploadFile(transportData);
    }).catch((err: any) => {
      this.errorMonitoringService.sendError(err, 'uploadFile');
      this.helperService.presentMessageAlert('Mensaje', 'No se ha podido cargar el archivo, por favor intente de nuevo');
    });
  }

  /**
   * @description Obtiene la imagen cargada desde el formulario
   *
   * @author Alejandro Rangel Moreno <erangel@asesoftware.com>
   * @date 2020-05-23
   * @param {*} [value]
   * @returns 
   * @memberof FilesComponent
   */
  async obtainImage(value?: any) {
    // Si no ha seleccionado un archivo
    if (!value) {
      this.errorMonitoringService.sendError('No se pudo cargar el archivo', 'obtainImage');
      return this.helperService.presentMessageAlert('Mensaje', 'No se ha podido cargar el archivo, por favor intente de nuevo');
    }

    let objectFile = value.target.files[0]
    // Si ocurrió algún problema al cargar el archivo y llega nulo
    if (!objectFile) return this.helperService.presentMessageAlert('Alerta', 'Ocurrió un problema y no se puede cargar el archivo seleccionado, intente de nuevo.');

    // Se crea el objeto para guardar en base de datos y controlarlo
    this.fileObject = {
      originalName: objectFile.name,
      name: moment.tz('America/Bogota').format('YYYYMMDDHHmmss'),
      extension: this.getFileExtension(objectFile.name),
      type: objectFile.type,
      size: objectFile.size,
      url: '',
      folder: this.folder.join('/'),
    } as FileObject;

    // Evitar cargar la misma imagen dos veces
    if (this.fileStore && this.fileStore.some(file => file.originalName === this.fileObject.originalName)) return this.helperService.presentMessageAlert('Alerta', 'Ya existe un archivo cargado con este mismo nombre');
    if (this.processingFiles.some(file => file.originalName === this.fileObject.originalName)) return this.helperService.presentMessageAlert('Alerta', 'Ya existe un archivo cargado con este mismo nombre');
    // Si la imagen es superior a 5 MB
    if (this.fileObject.size > this.maxSizeOfFile) return this.helperService.presentMessageAlert('¡Sobrepasaste el tamaño de archivo!', 'El tamaño máximo para el archivo es de 5MB','','tamano');
    // Es un tipo de archivo permitido
    if (!this.extensionIsValid(this.fileObject.extension)) return this.helperService.presentMessageAlert('Alerta', 'No es un tipo de archivo admitido, por favor intente de nuevo.');

    this.uploadFile({ metadata: this.fileObject, file: objectFile });
  }

  /**
   * @description Valida si la extensión del archivo es válida
   *
   * @author Alejandro Rangel Moreno <erangel@asesoftware.com>
   * @date 2020-05-23
   * @param {string} [fileExtension]
   * @returns {boolean}
   * @memberof FilesComponent
   */
  extensionIsValid(fileExtension?: string): boolean {
    if (!fileExtension) return false;
    var ext = '.' + fileExtension;
    return this.accept.split(',').includes(ext);
  }

  /**
   * @description Obtiene la extensión de un nombre de archivo proporcionado
   *
   * @author Alejandro Rangel Moreno <erangel@asesoftware.com>
   * @date 2020-05-23
   * @param {string} filename
   * @returns {string}
   * @memberof FilesComponent
   */
  getFileExtension(filename: string): string {
    var parts = filename.split('.');
    return parts.pop();
  }

  /**
   * @description Obtiene el nombre sin extensión de archivo proporcionado
   *
   * @author Alejandro Rangel Moreno <erangel@asesoftware.com>
   * @date 2020-05-23
   * @param {string} filename
   * @returns {string}
   * @memberof FilesComponent
   */
  getFileName(filename: string): string {
    var parts = filename.split('.');
    parts.pop();
    return parts.join();
  }

  /**
   * @description Obtiene un listado de tipos de archivos separados por coma
   *
   * @author Alejandro Rangel Moreno <erangel@asesoftware.com>
   * @date 2020-05-23
   * @param {string[]} types
   * @returns {string}
   * @memberof FilesComponent
   */
  getTypesOfFiles(types: string[]): string {
    let tempArray = [];
    types.forEach(x => tempArray.push(`.${x}`));
    return tempArray.join(',');
  }

  /**
   * @description Evento al seleccionar el archivo
   *
   * @author Alejandro Rangel Moreno <erangel@asesoftware.com>
   * @date 2020-05-23
   * @memberof FilesComponent
   */
  selectFile() {
    document.getElementById('file-input').click();
  }

  /**
   * @description Evento para salir del modal
   *
   * @author Alejandro Rangel Moreno <erangel@asesoftware.com>
   * @date 2020-05-23
   * @memberof FilesComponent
   */
  getOut() {
    this.modalController.dismiss();
  }
}